import Dialog from "@/molecules/dialog";
import { styles } from "./style";
import {
  Box,
  Button,
  Stack,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
} from "@/common/styles/style";

import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteConfirmModal from "@/organisms/deleteConfirmModal";
import {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

interface RegisterTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onRegisterComplete: () => void | Promise<void>;
  machineId: number;
}

type UploadType = "template" | "mapping";

interface Template {
  id: number;
  templateName: string;
  createAt?: string;
}

interface ApiResponse<T = null> {
  message: string;
  errorCode: number | null;
  result: T | null;
}

const apiRequest = async <T = null,>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> => {
  const url = path;

  console.log("API URL:", url);
  console.log("API method:", options.method ?? "GET");

  try {
    const response = await fetch(url, {
      ...options,
      credentials: "include",
    });

    let data: ApiResponse<T>;

    try {
      data = await response.json();
    } catch {
      throw new Error("サーバーから不正なレスポンスが返されました。");
    }

    if (!response.ok) {
      throw new Error(data.message || "APIエラーが発生しました。");
    }

    return data;
  } catch (error) {
    console.error("apiRequestエラー:", error);
    throw error;
  }
};

export default function RegisterTemplateDialog({
  open,
  onClose,
  onRegisterComplete,
  machineId,
}: RegisterTemplateDialogProps) {
  const [registerType, setRegisterType] = useState<"new" | "update">("new");

  const [templateName, setTemplateName] = useState("");

  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);

  const [editTemplateName, setEditTemplateName] = useState<string>("");

  const [isTemplateDeleted, setIsTemplateDeleted] = useState(false);

  const [originalTemplateName, setOriginalTemplateName] = useState<string>("");

  const [templateFile, setTemplateFile] = useState<File | null>(null);

  const [mappingFile, setMappingFile] = useState<File | null>(null);

  // ドラッグ状態
  const [isTemplateDragOver, setIsTemplateDragOver] = useState(false);

  const [isMappingDragOver, setIsMappingDragOver] = useState(false);

  const [templates, setTemplates] = useState<Template[]>([]);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [deletingTemplate, setDeletingTemplate] = useState<number | null>(null);

  // ファイルinput
  const templateInputRef = useRef<HTMLInputElement>(null);

  const mappingInputRef = useRef<HTMLInputElement>(null);

  //テンプレート名比較
  const isTemplateNameChanged =
    editTemplateName.trim() !== originalTemplateName.trim();

  // 登録・更新ボタン活性状態
  const isRegisterDisabled =
    registerType === "new"
      ? !templateName.trim() || !templateFile || !mappingFile
      : !isTemplateDeleted &&
        (!editTemplateName.trim() ||
          (!isTemplateNameChanged && !templateFile && !mappingFile));

  const fetchTemplates = useCallback(async (): Promise<Template[]> => {
    const data = await apiRequest<Template[]>(
      `/api/machines/${machineId}/report-templates`,
      {
        method: "GET",
      },
    );

    const fetchedTemplates = Array.isArray(data.result) ? data.result : [];

    setTemplates(fetchedTemplates);

    setSelectedTemplate((currentSelectedTemplate) => {
      const exists = fetchedTemplates.some(
        (template) => template.id === currentSelectedTemplate,
      );

      return exists
        ? currentSelectedTemplate
        : (fetchedTemplates[0]?.id ?? null);
    });

    return fetchedTemplates;
  }, [machineId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    fetchTemplates().catch((error) => {
      console.error("テンプレート一覧取得エラー:", error);

      alert(
        error instanceof Error
          ? error.message
          : "テンプレート一覧の取得に失敗しました。",
      );
    });
  }, [open, fetchTemplates]);

  const handleSelectTemplate = (templateId: number) => {
    if (isTemplateDeleted) {
      return;
    }

    const template = templates.find((item) => item.id === templateId);

    if (!template) {
      return;
    }

    setSelectedTemplate(template.id);
    setEditTemplateName(template.templateName);
    setOriginalTemplateName(template.templateName);

    setTemplateFile(null);
    setMappingFile(null);
  };

  // テンプレート名変更
  const handleEditTemplateName = (event: ChangeEvent<HTMLInputElement>) => {
    // 削除対象選択中は変更不可
    if (isTemplateDeleted) {
      return;
    }

    setEditTemplateName(event.target.value);
  };

  // 削除チェック変更
  const handleTemplateDeleteChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setIsTemplateDeleted(checked);
  };

  const handleOpenDeleteDialog = () => {
    if (selectedTemplate === null) {
      return;
    }

    setDeletingTemplate(selectedTemplate);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteTemplate = async () => {
    if (deletingTemplate === null) {
      return;
    }

    try {
      await apiRequest(
        `/api/machines/${machineId}/report-templates/${deletingTemplate}`,
        {
          method: "DELETE",
        },
      );

      const remainingTemplates = await fetchTemplates();

      if (remainingTemplates.length === 0) {
        setRegisterType("new");
        setSelectedTemplate(null);
        setEditTemplateName("");
        setOriginalTemplateName("");
      } else if (selectedTemplate === deletingTemplate) {
        const nextTemplate = remainingTemplates[0];

        setSelectedTemplate(nextTemplate.id);
        setEditTemplateName(nextTemplate.templateName);
        setOriginalTemplateName(nextTemplate.templateName);
      }

      setIsTemplateDeleted(false);
      setTemplateFile(null);
      setMappingFile(null);
      setDeletingTemplate(null);
      setIsDeleteDialogOpen(false);

      await onRegisterComplete();
    } catch (error) {
      console.error("テンプレート削除エラー:", error);

      alert(
        error instanceof Error
          ? error.message
          : "テンプレートの削除に失敗しました。",
      );
    }
  };

  const handleRegisterTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const type = event.target.value as "new" | "update";

    setRegisterType(type);

    // 新規登録
    if (type === "new") {
      setTemplateName("");
      setIsTemplateDeleted(false);

      setTemplateFile(null);
      setMappingFile(null);

      setDeletingTemplate(null);
      setIsDeleteDialogOpen(false);
    }

    // 更新
    if (type === "update") {
      const initialTemplate = templates[0];

      setSelectedTemplate(initialTemplate?.id ?? null);
      setEditTemplateName(initialTemplate?.templateName ?? "");
      setOriginalTemplateName(initialTemplate?.templateName ?? "");
      setIsTemplateDeleted(false);

      setTemplateFile(null);
      setMappingFile(null);

      setDeletingTemplate(null);
      setIsDeleteDialogOpen(false);
    }
  };

  // ファイル形式チェック
  const validateFile = (type: UploadType, file: File): boolean => {
    if (type === "template") {
      const fileName = file.name.toLowerCase();

      if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".xlsm")) {
        alert("帳票テンプレートはxlsx、xlsm形式でアップロードしてください。");
        return false;
      }
    } else {
      if (!file.name.toLowerCase().endsWith(".json")) {
        alert("マッピング情報はJSON形式でアップロードしてください。");
        return false;
      }
    }

    return true;
  };

  // ファイル選択
  const handleFileSelect =
    (type: UploadType) => (event: ChangeEvent<HTMLInputElement>) => {
      // 削除対象選択中は変更不可
      if (isTemplateDeleted) {
        event.target.value = "";
        return;
      }

      const file = event.target.files?.[0];

      if (!file) {
        return;
      }

      // ファイル形式チェック
      if (!validateFile(type, file)) {
        event.target.value = "";
        return;
      }

      if (type === "template") {
        setTemplateFile(file);
      } else {
        setMappingFile(file);
      }

      event.target.value = "";
    };

  // ドロップ処理
  const handleDrop =
    (type: UploadType) => (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (type === "template") {
        setIsTemplateDragOver(false);
      } else {
        setIsMappingDragOver(false);
      }

      if (isTemplateDeleted) {
        return;
      }

      const file = event.dataTransfer.files?.[0];

      if (!file) {
        return;
      }

      // ファイル形式チェック
      if (!validateFile(type, file)) {
        return;
      }

      if (type === "template") {
        setTemplateFile(file);
      } else {
        setMappingFile(file);
      }
    };

  // ドラッグ開始
  const handleDragOver =
    (type: UploadType) => (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      // 削除対象選択中はドラッグ状態にしない
      if (isTemplateDeleted) {
        return;
      }

      if (type === "template") {
        setIsTemplateDragOver(true);
      } else {
        setIsMappingDragOver(true);
      }
    };

  // ドラッグ終了
  const handleDragLeave =
    (type: UploadType) => (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (type === "template") {
        setIsTemplateDragOver(false);
      } else {
        setIsMappingDragOver(false);
      }
    };

  // アップロードエリアクリック
  const handleUploadAreaClick = (type: UploadType) => {
    // 削除対象選択中は変更不可
    if (isTemplateDeleted) {
      return;
    }

    if (type === "template") {
      templateInputRef.current?.click();
    } else {
      mappingInputRef.current?.click();
    }
  };

  // フォーム初期化
  const resetForm = () => {
    setRegisterType("new");
    setTemplateName("");

    const initialTemplate = templates[0];

    setSelectedTemplate(initialTemplate?.id ?? null);
    setEditTemplateName(initialTemplate?.templateName ?? "");
    setOriginalTemplateName(initialTemplate?.templateName ?? "");
    setIsTemplateDeleted(false);

    setTemplateFile(null);
    setMappingFile(null);

    setIsTemplateDragOver(false);
    setIsMappingDragOver(false);

    setDeletingTemplate(null);
    setIsDeleteDialogOpen(false);
  };

  const handleNewRegister = async () => {
    console.log("handleNewRegister開始");

    if (!templateFile || !mappingFile) {
      console.log("ファイル不足");
      return;
    }

    const formData = new FormData();
    formData.append("templateName", templateName.trim());
    formData.append("templateFile", templateFile);
    formData.append("mappingFile", mappingFile);

    console.log("API呼び出し開始");

    try {
      const result = await apiRequest(
        `/api/machines/${machineId}/report-templates`,
        {
          method: "POST",
          body: formData,
        },
      );

      console.log("API成功:", result);

      resetForm();
      await onRegisterComplete();
    } catch (error) {
      console.error("テンプレート登録エラー:", error);

      alert(
        error instanceof Error
          ? error.message
          : "テンプレートの登録に失敗しました。",
      );
    }
  };

  const handleUpdate = async () => {
    if (isTemplateDeleted) {
      handleOpenDeleteDialog();
      return;
    }

    if (selectedTemplate === null) {
      return;
    }

    const formData = new FormData();

    formData.append("templateName", editTemplateName.trim());

    if (templateFile) {
      formData.append("templateFile", templateFile);
    }

    if (mappingFile) {
      formData.append("mappingFile", mappingFile);
    }

    try {
      await apiRequest(
        `/api/machines/${machineId}/report-templates/${selectedTemplate}`,
        {
          method: "POST",
          body: formData,
        },
      );

      resetForm();
      await onRegisterComplete();
    } catch (error) {
      console.error("テンプレート更新エラー:", error);

      alert(
        error instanceof Error
          ? error.message
          : "テンプレートの更新に失敗しました。",
      );
    }
  };

  const handleRegister = () => {
    if (isRegisterDisabled) {
      return;
    }

    if (registerType === "new") {
      void handleNewRegister();
      return;
    }

    void handleUpdate();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const deletingTemplateData = templates.find(
    (template) => template.id === deletingTemplate,
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        title="テンプレート登録"
        PaperProps={{
          sx: styles.dialogPaper,
        }}
        sx={styles.container}
        contentChildren={
          <Box sx={styles.content}>
            {/* 登録区分 */}
            <Typography sx={styles.label}>登録区分</Typography>

            <RadioGroup
              row
              value={registerType}
              onChange={handleRegisterTypeChange}
              sx={styles.radioGroup}
            >
              <FormControlLabel
                value="new"
                control={<Radio size="small" />}
                label="新規登録"
              />

              <FormControlLabel
                value="update"
                control={
                  <Radio size="small" disabled={templates.length === 0} />
                }
                label="更新"
              />
            </RadioGroup>

            {registerType === "new" ? (
              <>
                {/* 新規登録：テンプレート名 */}
                <Typography sx={styles.label}>テンプレート名</Typography>

                <TextField
                  fullWidth
                  value={templateName}
                  onChange={(event) => {
                    setTemplateName(event.target.value);
                  }}
                  size="small"
                  sx={styles.templateName}
                />
              </>
            ) : (
              <>
                {/* 更新：テンプレート選択 */}
                <Typography sx={styles.label}>テンプレート選択</Typography>

                <Box sx={styles.templateTableWrapper}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell width="15%" />
                        <TableCell>名前</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {templates.map((template) => (
                        <TableRow key={template.id}>
                          <TableCell>
                            <Radio
                              size="small"
                              checked={selectedTemplate === template.id}
                              onChange={() => handleSelectTemplate(template.id)}
                              disabled={isTemplateDeleted}
                            />
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {template.templateName}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                {/* 更新：テンプレート名 */}
                <Typography sx={styles.label}>テンプレート名編集</Typography>

                <TextField
                  fullWidth
                  value={editTemplateName}
                  onChange={handleEditTemplateName}
                  disabled={isTemplateDeleted}
                  size="small"
                  sx={styles.templateName}
                />

                {/* 更新：削除 */}
                <Typography sx={styles.label}>削除</Typography>

                <Box
                  sx={{
                    width: "100%",
                    marginBottom: 2,
                    opacity: isTemplateDeleted ? 0.9 : 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isTemplateDeleted}
                        onChange={handleTemplateDeleteChange}
                      />
                    }
                    label="このテンプレートを削除する"
                  />
                </Box>
              </>
            )}

            {/* 帳票テンプレート */}
            <Typography sx={styles.label}>帳票テンプレート</Typography>

            <Box
              sx={{
                ...styles.uploadArea,
                ...(isTemplateDragOver &&
                  !isTemplateDeleted &&
                  styles.uploadAreaDragOver),
                ...(isTemplateDeleted && {
                  opacity: 0.5,
                  cursor: "not-allowed",
                  pointerEvents: "none",
                }),
              }}
              onClick={() => handleUploadAreaClick("template")}
              onDragOver={handleDragOver("template")}
              onDragLeave={handleDragLeave("template")}
              onDrop={handleDrop("template")}
            >
              <input
                ref={templateInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                hidden
                onChange={handleFileSelect("template")}
              />

              {templateFile ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={0.5}
                  sx={styles.uploadContent}
                >
                  <CheckCircleOutlineIcon sx={styles.uploadedFileIcon} />

                  <Typography sx={styles.fileName}>
                    {templateFile.name}
                  </Typography>
                </Stack>
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={styles.uploadContent}
                >
                  <Box sx={styles.fileIcon}>
                    <InsertDriveFileOutlinedIcon />
                  </Box>

                  <Typography sx={styles.uploadDescription}>
                    ※xlsx、xlsm形式でアップロードしてください
                  </Typography>
                </Stack>
              )}
            </Box>

            {/* マッピング情報 */}
            <Typography sx={styles.label}>マッピング情報</Typography>

            <Box
              sx={{
                ...styles.uploadArea,
                ...(isMappingDragOver &&
                  !isTemplateDeleted &&
                  styles.uploadAreaDragOver),
                ...(isTemplateDeleted && {
                  opacity: 0.5,
                  cursor: "not-allowed",
                  pointerEvents: "none",
                }),
              }}
              onClick={() => handleUploadAreaClick("mapping")}
              onDragOver={handleDragOver("mapping")}
              onDragLeave={handleDragLeave("mapping")}
              onDrop={handleDrop("mapping")}
            >
              <input
                ref={mappingInputRef}
                type="file"
                accept=".json,application/json"
                hidden
                onChange={handleFileSelect("mapping")}
              />

              {mappingFile ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={0.5}
                  sx={styles.uploadContent}
                >
                  <CheckCircleOutlineIcon sx={styles.uploadedFileIcon} />

                  <Typography sx={styles.fileName}>
                    {mappingFile.name}
                  </Typography>
                </Stack>
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={styles.uploadContent}
                >
                  <Box sx={styles.fileIcon}>
                    <InsertDriveFileOutlinedIcon />
                  </Box>

                  <Typography sx={styles.uploadDescription}>
                    ※JSON形式でアップロードしてください
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>
        }
        actionsChildren={
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={styles.buttonArea}
          >
            {/* キャンセル */}
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={styles.cancelButton}
            >
              <Typography variant="button">キャンセル</Typography>
            </Button>

            {/* 登録 / 更新 */}
            <Button
              variant="contained"
              onClick={handleRegister}
              disabled={isRegisterDisabled}
              sx={styles.registerButton}
            >
              <Typography variant="button">
                {registerType === "new" ? "登録" : "更新"}
              </Typography>
            </Button>
          </Stack>
        }
      />

      {/* 削除確認ダイアログ */}
      <DeleteConfirmModal
        open={isDeleteDialogOpen}
        deleteMessage={deletingTemplateData?.templateName ?? ""}
        onClose={handleCloseDeleteDialog}
        handleDelete={handleDeleteTemplate}
      />
    </>
  );
}
